<!-- Template con estilos del dashboard -->
<div class="app-page">
  <div class="app-page-content">
    <section class="app-card">
      
      <!-- Header -->
      <header class="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div class="flex flex-col gap-1">
          <h1 class="section-title">Usuarios</h1>
          <p class="section-subtitle">
            Gestión de usuarios, roles y permisos
          </p>
        </div>
        
        <div class="flex flex-wrap items-center gap-2">
          <span class="app-badge">
            <strong>{{ totalRecords() }}</strong> usuarios
          </span>
          <span 
            class="app-badge"
            [ngClass]="{
              'app-badge-primary': !paginate
            }">
            {{ paginate ? 'Paginado' : 'Todos' }}
          </span>
        </div>
      </header>

    <p-confirmDialog [baseZIndex]="10000"></p-confirmDialog>

    <p-table #dt [value]="usuarios()" [columns]="columnsTable" dataKey="id" sortMode="multiple"
      [(selection)]="selectedRows" [loading]="loading()" [rowHover]="true" [lazy]="true" (onLazyLoad)="loadLazy($event)"
      styleClass="app-table-container" [paginator]="paginate" [rows]="nroFilasPorPagina"
      [totalRecords]="totalRecords()" [filterDelay]="500" [scrollable]="false"
      [globalFilterFields]="['email','nombre','apellido']"
      currentPageReportTemplate="{first} - {last} de {totalRecords} registros" [showCurrentPageReport]="true"
      [rowsPerPageOptions]="rowsPerPageOptions" responsiveLayout="stack" [breakpoint]="'960px'">

      <!-- Toolbar Caption -->
      <ng-template pTemplate="caption">
        <div class="flex flex-col gap-4 py-2">
          <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <!-- Search Input -->
            <div class="w-full md:max-w-96">
              <span class="p-input-icon-left w-full search-input-wrapper">
                <i class="pi pi-search search-icon"></i>
                <input type="search" pInputText [ngModel]="filterValue()" (ngModelChange)="onGlobalFilter($event, dt)"
                  placeholder="Buscar por email o nombre" class="w-full" />
              </span>
            </div>

            <!-- Action Buttons -->
            <div class="flex flex-wrap items-center gap-2.5">
              <button pButton type="button" [label]="paginate ? 'Paginado' : 'Todos'"
                [icon]="paginate ? 'pi pi-lock' : 'pi pi-lock-open'" class="p-button-outlined p-button-sm"
                (click)="paginate = !paginate; cargar()">
              </button>
              <button pButton type="button" label="Limpiar" icon="pi pi-filter-slash"
                class="p-button-outlined p-button-sm" (click)="clear(dt)">
              </button>
              <button pButton type="button" label="Nuevo usuario" icon="pi pi-user-plus" class="p-button-sm"
                (click)="navigateToCreateUser()">
              </button>
            </div>
          </div>
        </div>
      </ng-template>

      <!-- Table Header -->
      <ng-template pTemplate="header" let-columns>
        <tr>
          <th class="w-16 px-4 py-3.5 text-center">
            <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
          </th>
          @for (col of columns; track col.field) {
          <th class="px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wide"
            [class.text-center]="col.field === 'enabled' || col.field === 'accion'"
            [pSortableColumn]="col.sortable ? col.field : ''">
            <div class="flex items-center justify-between gap-2">
              <span>{{ col.header }}</span>
              <div class="flex items-center gap-2">
                @if (col.sortable) {
                <p-sortIcon [field]="col.field"></p-sortIcon>
                }
                @if (col.filtrable) {
                <p-columnFilter [type]="col.fieldType || 'text'" [field]="col.field" display="menu"
                  [showMatchModes]="false" [matchMode]="col.fieldType === 'boolean' ? 'equals' : 'contains'" [showOperator]="false" [showAddButton]="false">
                </p-columnFilter>
                }
              </div>
            </div>
          </th>
          }
        </tr>
      </ng-template>

      <!-- Table Body -->
      <ng-template pTemplate="body" let-usuario let-columns="columns">
        <tr class="transition-colors" [pSelectableRow]="usuario">
          <td class="w-16 px-4 py-4 text-center">
            <p-tableCheckbox [value]="usuario"></p-tableCheckbox>
          </td>
          @for (col of columns; track col.field) {
          <td class="px-4 py-4" [class.text-center]="col.field === 'enabled' || col.field === 'accion'">
            @switch (col.field) {
            @case ('username') {
            <div class="flex items-center gap-3.5">
              <div
                class="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full text-base font-semibold uppercase"
                [style.background-color]="'hsl(var(--surface-2))'"
                [style.color]="'hsl(var(--text))'"
                [ngClass]="{'border border-dashed': !usuario.avatar}"
                [style.border-color]="!usuario.avatar ? 'hsl(var(--border))' : 'transparent'">
                @if (usuario.avatar) {
                <img [src]="usuario.avatar | authImage | async" [alt]="usuario.username" class="h-full w-full object-cover" loading="lazy"
                  decoding="async" />
                } @else {
                <span>{{ usuario.username?.charAt(0) | uppercase }}</span>
                }
              </div>
              <div class="flex flex-col gap-0.5">
                <span class="text-sm font-medium" [style.color]="'hsl(var(--text))'">{{ usuario.username }}</span>
                @if (usuario.fullName && usuario.fullName !== usuario.username) {
                <span class="text-xs text-muted">{{ usuario.fullName }}</span>
                }
              </div>
            </div>
            }
            @case ('email') {
            <a [href]="'mailto:' + usuario.email" class="text-sm font-medium no-underline hover:underline"
              [style.color]="'hsl(var(--primary))'">
              {{ usuario.email }}
            </a>
            }
            @case ('roles') {
            <div class="flex flex-wrap gap-1.5">
              @if (usuario.roles?.length) {
              @for (role of usuario.roles; track role) {
              <span class="app-badge text-xs"
                [ngClass]="{
                  'app-badge-primary': getRoleVariant(role) === 'role-chip--admin',
                  'app-badge-success': getRoleVariant(role) === 'role-chip--user',
                  'app-badge-warning': getRoleVariant(role) === 'role-chip--super',
                  'app-badge': getRoleVariant(role) === 'role-chip--default'
                }">
                {{ getRoleDisplay(role) }}
              </span>
              }
              } @else {
              <span class="text-muted text-sm">Sin rol</span>
              }
            </div>
            }
            @case ('enabled') {
            <div class="inline-flex items-center gap-2.5">
              <p-toggleSwitch [name]="'enabled_' + usuario.id" [(ngModel)]="usuario.enabled"
                pTooltip="Activar / Desactivar" tooltipPosition="top" (onChange)="toggleUsuarioEnabled(usuario)"
                class="[&_.p-toggleswitch]:h-6 [&_.p-toggleswitch]:w-11 [&_.p-toggleswitch]:rounded-full [&_.p-toggleswitch-slider]:rounded-full [&_.p-toggleswitch-slider]:bg-slate-400/50 [&_.p-toggleswitch-slider]:transition-all [&_.p-toggleswitch-slider::before]:m-0.5 [&_.p-toggleswitch-slider::before]:h-5 [&_.p-toggleswitch-slider::before]:w-5 [&_.p-toggleswitch-slider::before]:rounded-full [&_.p-toggleswitch-slider::before]:bg-white [&_.p-toggleswitch-slider::before]:shadow-[0_4px_12px_rgba(15,23,42,0.18)] [&_.p-toggleswitch-slider::before]:transition-transform [&_.p-toggleswitch-checked_.p-toggleswitch-slider]:bg-green-500/90 [&_.p-toggleswitch-checked_.p-toggleswitch-slider::before]:translate-x-5">
              </p-toggleSwitch>
              <span class="text-xs font-medium" [style.color]="usuario.enabled ? 'hsl(var(--success))' : 'hsl(var(--muted))'">
                {{ usuario.enabled ? 'Activo' : 'Inactivo' }}
              </span>
            </div>
            }
            @case ('accion') {
            <div class="flex justify-center">
              <button
                pButton
                type="button"
                icon="pi pi-pencil"
                class="p-button-rounded p-button-text p-button-sm"
                pTooltip="Editar usuario"
                tooltipPosition="top"
                (click)="usuario.id && editarUsuario(usuario.id)">
              </button>
            </div>
            }
            @default {
            <span class="text-sm text-muted">{{ usuario[col.field] || '-' }}</span>
            }
            }
          </td>
          }
        </tr>
      </ng-template>

      <!-- Empty State -->
      <ng-template pTemplate="emptymessage">
        <tr>
          <td [attr.colspan]="columnsTable.length + 1" class="text-center py-8">
            <div class="flex flex-col items-center gap-3">
              <i class="pi pi-users text-5xl text-muted"></i>
              <p class="text-muted">No se encontraron usuarios</p>
              <button pButton type="button" label="Nuevo usuario" icon="pi pi-user-plus"
                class="p-button-sm"
                (click)="navigateToCreateUser()">
              </button>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
    </section>
  </div>
</div>
